import { useState, useRef, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

import type { DrawerPanelHandle } from '@/components/layouts/Panels/Panels';

export interface UsePanelDrawerParams {
  /** The currently selected item ID from URL (e.g., project ID, dashboard ID) */
  selectedId: string | null;
}

export interface UsePanelDrawerReturn {
  /** Whether the drawer panel is currently open */
  isPanelOpen: boolean;
  /** Value for Panels component expandDrawer prop (true | undefined) */
  expandDrawer: true | undefined;
  /** Ref for the drawer component (focus management) */
  drawerRef: RefObject<DrawerPanelHandle | null>;
  /** Opens the drawer panel */
  openPanel: () => void;
  /** Opens the drawer and focuses it after animation completes */
  openPanelAndFocus: () => void;
  /** Closes the drawer panel */
  closePanel: () => void;
  /** Handles Escape key - returns focus to table row */
  handleEscapeFromDrawer: () => void;
}

/**
 * Manages drawer/panel state with URL synchronization and keyboard navigation.
 *
 * Handles:
 * - Drawer open/close state
 * - Focus management with animation timing
 * - URL parameter synchronization
 * - Escape key handling to return focus to table
 *
 * @example
 * ```tsx
 * const {
 *   isPanelOpen,
 *   expandDrawer,
 *   drawerRef,
 *   openPanel,
 *   openPanelAndFocus,
 *   closePanel,
 *   handleEscapeFromDrawer,
 * } = usePanelDrawer({ selectedId: projectId });
 *
 * <Panels
 *   expandDrawer={expandDrawer}
 *   onDrawerCollapse={closePanel}
 *   drawerRef={drawerRef}
 *   onEscapeFromDrawer={handleEscapeFromDrawer}
 * >
 * ```
 */
export const usePanelDrawer = ({
  selectedId,
}: UsePanelDrawerParams): UsePanelDrawerReturn => {
  const [isPanelOpenState, setIsPanelOpenState] = useState(false);
  const prevIdRef = useRef<string | null>(null);
  const drawerRef = useRef<DrawerPanelHandle>(null);

  const openPanel = useCallback(() => {
    setIsPanelOpenState(true);
  }, []);

  const openPanelAndFocus = useCallback(() => {
    setIsPanelOpenState(true);
    // Double RAF ensures focus happens after: (1) React render + DOM update, (2) panel expansion animation starts
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        drawerRef.current?.focus();
      });
    });
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpenState(false);
  }, []);

  const handleEscapeFromDrawer = useCallback(() => {
    // Focus the selected row in the table using the ID from URL
    if (!selectedId) return;

    // Find the row by data-id attribute (MUI DataGrid pattern)
    // This might need to be more flexible if we want to focus different elements or use a different table implementation
    const escapedId = CSS.escape(selectedId);
    const row = document.querySelector(`[role="row"][data-id="${escapedId}"]`);
    if (row) {
      const firstCell = row.querySelector('[role="gridcell"]') as HTMLElement;
      firstCell?.focus();
    }
  }, [selectedId]);

  // Coordinate panel state with URL changes
  useEffect(() => {
    const hadId = prevIdRef.current !== null;
    const hasId = selectedId !== null;

    // Open panel when URL gets an ID (page load, manual URL edit)
    if (!hadId && hasId) {
      setIsPanelOpenState(true);
    }
    // Close panel when URL loses ID (back button to no-selection state)
    else if (hadId && !hasId) {
      setIsPanelOpenState(false);
    }

    prevIdRef.current = selectedId;
  }, [selectedId]);

  const isPanelOpen = isPanelOpenState;
  const expandDrawer = isPanelOpenState ? true : undefined;

  return {
    isPanelOpen,
    expandDrawer,
    drawerRef,
    openPanel,
    openPanelAndFocus,
    closePanel,
    handleEscapeFromDrawer,
  };
};
